import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSelect } from '@chilecompra/react-kit'
import { Button, Select } from '@chilecompra/react-kit/components'
import DownloadsNavButton from '../../components/Navigation/DownloadsNavButton'
import Toast from '../../components/Toast'
import { generateYears } from '../../modules/utils/years'
import {
  Description,
  GridContainer,
  GridItem,
  Root,
  Subtitle,
  Title
} from '../DownloadLargePurchaseProcessesSection/DownloadLargePurchaseProcessesSection.styles'

const INSTITUTION_OPTIONS = [
  { value: 'all', name: 'Todas' },
  { value: 'ministerio', name: 'Ministerio' },
  { value: 'servicio', name: 'Servicio' },
  { value: 'municipalidad', name: 'Municipalidad' }
]

const CONTRACT_STATE_OPTIONS = [
  { value: 'all', name: 'Todos' },
  { value: 'vigente', name: 'Vigente' },
  { value: 'finalizado', name: 'Finalizado' },
  { value: 'cancelado', name: 'Cancelado' }
]

/**
 * The DownloadContractsSection's container.
 */
const DownloadContractsSection = () => {
  const { endYear } = useSelector(state => state.topNavbar)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })
  const { value: contractStateValue, onChange: handleChangeContractState } = useSelect({
    initialValue: 'all'
  })
  const { value: institutionValue, onChange: handleChangeInstitution } = useSelect({
    initialValue: 'all'
  })
  const yearsOptions = useMemo(() => generateYears(endYear), [endYear])
  const [toast, setToast] = useState({
    visible: false,
    variant: 'success',
    title: 'Descarga exitosa',
    message: 'El archivo se está descargando correctamente.',
    key: 0
  })

  const handleCloseToast = useCallback(() => {
    setToast(previous => ({ ...previous, visible: false }))
  }, [])

  const handleClickDownload = useCallback(() => {
    const selectedContractState =
      CONTRACT_STATE_OPTIONS.find(option => option.value === contractStateValue)?.name || 'Todos'
    const selectedInstitution = INSTITUTION_OPTIONS.find(option => option.value === institutionValue)?.name || 'Todas'
    const fileName = `Contratos_${yearValue}_${selectedInstitution.replace(/\s+/g, '_')}_${selectedContractState.replace(/\s+/g, '_')}.csv`
    const csvRows = [
      ['Año', 'Institución', 'Estado de contrato'],
      [yearValue, selectedInstitution, selectedContractState]
    ]
    const csvContent = csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\r\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const anchorElement = document.createElement('a')

    anchorElement.href = url
    anchorElement.download = fileName
    document.body.appendChild(anchorElement)
    anchorElement.click()
    anchorElement.remove()
    window.URL.revokeObjectURL(url)

    setToast(previous => ({
      ...previous,
      visible: true,
      variant: 'success',
      title: 'Descarga exitosa',
      message: `Descargando ${fileName}`,
      key: previous.key + 1
    }))
  }, [contractStateValue, institutionValue, yearValue])

  return (
    <Root>
      <Toast
        visible={toast.visible}
        variant={toast.variant}
        title={toast.title}
        message={toast.message}
        duration={4000}
        onClose={handleCloseToast}
        closeable
        toastKey={toast.key}
      />
      <GridContainer padding="0 0 26px">
        <DownloadsNavButton />
        <GridItem xs={10} sm={11}>
          <Title>Contratos</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Subtitle>Detalle de Contratos</Subtitle>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Description>
            En esta sección puedes descargar contratos en formato CSV para análisis detallado fuera de la plataforma. La
            descarga incluye información de institución, proveedor, estado, fechas, montos y sanciones.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Subtitle>¿Qué encontrarás aquí?</Subtitle>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Description>Un archivo filtrable por Año, Institución y Estado del contrato.</Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Subtitle>¿Cómo utilizar esta sección?</Subtitle>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Description>
            Define los filtros requeridos y presiona “Descargar CSV” para obtener el universo exacto de contratos.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Subtitle>¿Qué contratos incluye esta descarga?</Subtitle>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Description>
            En términos generales, esta descarga considera adquisiciones que derivan en gestión contractual en el SGC,
            especialmente compras sobre 1.000 UTM y contrataciones de mayor complejidad. Si necesitas revisar el detalle
            público de contratos, puedes consultarlo directamente en el Sistema de Gestión de Contratos (SGC), vista
            ciudadanía: Revisar Contratos | Mercado Público. La plataforma de compras públicas y oportunidades de
            negocio del Estado de Chile.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer alignItems="center" spacing={2} padding="0 0 0px">
        <GridItem xs={12} md={3}>
          <Select label="AÑO" options={yearsOptions} value={yearValue} onChange={handleChangeYear} />
        </GridItem>
        <GridItem xs={12} md={3}>
          <Select
            label="INSTITUCIÓN"
            options={INSTITUTION_OPTIONS}
            value={institutionValue}
            onChange={handleChangeInstitution}
          />
        </GridItem>
        <GridItem xs={12} md={3}>
          <Select
            label="ESTADO DE CONTRATO"
            options={CONTRACT_STATE_OPTIONS}
            value={contractStateValue}
            onChange={handleChangeContractState}
          />
        </GridItem>
      </GridContainer>
      <GridContainer justifyContent="flex-start" padding="0 0 23px">
        <GridItem xs={12} md={3}>
          <Button color="primary" onClick={handleClickDownload}>
            Descargar CSV
          </Button>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default DownloadContractsSection
